import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import SelectPanel from '@lib/components/SelectPanel/SelectPanel';
import { SectorOption, SiteOption } from '@lib/components/SelectPanel/options';
import { GetLoadOptions } from '@lib/components/Select/types';
import { getOptionsArrayValidation } from '@lib/utils/validationUtils';
import {
  FieldChangeCallBackArgs,
  FieldItem,
  Values,
} from '@lib/interfaces/form';
import generateIdsFieldKey from '@lib/utils/generateIdsFieldKey';
import formStyles from '@lib/assets/styles/Form.module.scss';
import { materialSymbolsOutlined } from '@lib/enums/common';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import { isFilterFieldListed } from '@lib/components/ModalTableMultiselect/utils/filterFieldVisibility';
import { FieldNames } from '../enums';
import styles from './getFilterFields.module.scss';

export default function useFilterFields({
  getSectorsLoadOptions,
  getSitesLoadOptions,
  getEquipmentLoadOptions,
  getCategoriesLoadOptions,
  isFavoritedMode,
  isFullAdmin,
  isViewLocked,
  onToggleFavoritedMode,
}: {
  getSectorsLoadOptions: GetLoadOptions;
  getSitesLoadOptions: GetLoadOptions;
  getEquipmentLoadOptions: GetLoadOptions;
  getCategoriesLoadOptions: GetLoadOptions;
  isFavoritedMode: boolean;
  isFullAdmin?: boolean;
  isViewLocked?: boolean;
  onToggleFavoritedMode: () => void;
}): FieldItem[] {
  const { t } = useTranslation();
  // Premium filter args (Favorites, Sectors, Sites, Categories) are gated by
  // `documentation_management` on the BE. Lock policy:
  //   • Full Admin on a plan-locked company → keep the chip + selects but
  //     render them disabled, so the user sees what's gated and is nudged
  //     to upgrade.
  //   • Non-Full-Admin on a plan-locked company → hide entirely (no upgrade
  //     CTA they can act on, and stale values can't trigger
  //     UPGRADE_PLAN_REQUIRED).
  const isPremiumDisabled = !!isViewLocked && !!isFullAdmin;
  const isPremiumHidden = !!isViewLocked && !isFullAdmin;
  const premiumFieldNames = [
    FieldNames.Favorites,
    FieldNames.Sectors,
    FieldNames.Sites,
    FieldNames.Categories,
  ];
  const effectiveDisabledFields = isPremiumDisabled ? premiumFieldNames : [];
  const effectiveHiddenFields = isPremiumHidden ? premiumFieldNames : [];
  const premiumDisabledTooltipProps = isPremiumDisabled
    ? { body: t('upgrade-plan-to-unlock') }
    : undefined;
  return [
    {
      name: FieldNames.Favorites,
      label: '',
      customRender: () => (
        <Chip
          label={t('favorites')}
          leftIcon={isPremiumDisabled ? 'lock' : 'bookmark_star'}
          leftIconSymbolsOutlined
          leftIconFilled={!isPremiumDisabled}
          onClick={isPremiumDisabled ? undefined : onToggleFavoritedMode}
          rightIcon={
            !isPremiumDisabled && isFavoritedMode ? 'check_circled' : undefined
          }
          rightIconClassName={styles.blueIcon}
          size={ChipSizes.large}
          type={
            isPremiumDisabled ? ChipTypes.chipDisabled : ChipTypes.chipFilter
          }
          tooltip={isPremiumDisabled ? t('upgrade-plan-to-unlock') : undefined}
        />
      ),
      formItemClassName: formStyles.formColumnAuto,
      hideErrorsSpace: true,
      isVisible: !isFilterFieldListed(
        effectiveHiddenFields,
        FieldNames.Favorites,
      ),
      isOptionalFn: () => true,
    },
    {
      name: FieldNames.Sectors,
      label: t('sector'),
      element: SelectPanel,
      validation: getOptionsArrayValidation({ t }),
      formItemClassName: formStyles.formColumnAuto,
      hideErrorsSpace: true,
      isVisible: !isFilterFieldListed(
        effectiveHiddenFields,
        FieldNames.Sectors,
      ),
      componentProps: {
        defaultOptions: false,
        disabled: isFilterFieldListed(
          effectiveDisabledFields,
          FieldNames.Sectors,
        ),
        disabledTooltipProps: premiumDisabledTooltipProps,
        getLoadOptions: getSectorsLoadOptions,
        renderOption: SectorOption,
        isAsync: true,
        isMulti: true,
        showTabs: true,
        buttonProps: {
          leftIcon: 'share_location',
          leftIconClassName: materialSymbolsOutlined,
        },
      },
      fieldChangeCallBack: ({ setValue, name }: FieldChangeCallBackArgs) => {
        if (name === FieldNames.Sectors) {
          setValue(FieldNames.Sites, [], {
            shouldValidate: false,
          });
        }
      },
      isOptionalFn: () => false,
    },
    {
      name: FieldNames.Sites,
      label: t('site'),
      element: SelectPanel,
      validation: getOptionsArrayValidation({ t }),
      formItemClassName: formStyles.formColumnAuto,
      hideErrorsSpace: true,
      isVisible: !isFilterFieldListed(effectiveHiddenFields, FieldNames.Sites),
      componentProps: {
        defaultOptions: false,
        disabled: isFilterFieldListed(
          effectiveDisabledFields,
          FieldNames.Sites,
        ),
        disabledTooltipProps: premiumDisabledTooltipProps,
        getLoadOptions: getSitesLoadOptions,
        renderOption: SiteOption,
        isAsync: true,
        isMulti: true,
        showTabs: true,
        buttonProps: {
          leftIcon: 'home_pin',
          leftIconClassName: materialSymbolsOutlined,
        },
      },
      getFieldKeyFn: (values: Values) => {
        const rawSectorIds = _get(values, FieldNames.Sectors, []) as Array<
          { value?: string } | string
        >;
        return generateIdsFieldKey(rawSectorIds, {
          prefix: `${FieldNames.Sectors}-sites`,
          emptyKey: `${FieldNames.Sectors}-no-sites`,
        });
      },
      isOptionalFn: () => false,
    },
    {
      name: FieldNames.Equipment,
      label: t('asset'),
      element: SelectPanel,
      validation: getOptionsArrayValidation({ t }),
      formItemClassName: formStyles.formColumnAuto,
      hideErrorsSpace: true,
      isVisible: !isFilterFieldListed(
        effectiveHiddenFields,
        FieldNames.Equipment,
      ),
      componentProps: {
        defaultOptions: false,
        disabled: isFilterFieldListed(
          effectiveDisabledFields,
          FieldNames.Equipment,
        ),
        getLoadOptions: getEquipmentLoadOptions,
        isAsync: true,
        isMulti: true,
        showTabs: true,
        buttonProps: {
          leftIcon: 'inventory',
          leftIconClassName: materialSymbolsOutlined,
        },
      },
      isOptionalFn: () => false,
    },
    {
      name: FieldNames.Categories,
      label: t('category'),
      element: SelectPanel,
      validation: getOptionsArrayValidation({ t }),
      formItemClassName: formStyles.formColumnAuto,
      hideErrorsSpace: true,
      isVisible: !isFilterFieldListed(
        effectiveHiddenFields,
        FieldNames.Categories,
      ),
      componentProps: {
        defaultOptions: false,
        disabled: isFilterFieldListed(
          effectiveDisabledFields,
          FieldNames.Categories,
        ),
        disabledTooltipProps: premiumDisabledTooltipProps,
        getLoadOptions: getCategoriesLoadOptions,
        isAsync: true,
        isMulti: true,
        showTabs: true,
        buttonProps: {
          leftIcon: 'category',
          leftIconClassName: materialSymbolsOutlined,
        },
      },
      isOptionalFn: () => false,
    },
  ];
}

export { FieldNames } from '../enums';
